<script setup>
import { ClickStore } from "@/front/stores/click_store.js";
import { PlayerStore } from "@/front/stores/player_store.js";
import { VideoStore } from "@/front/stores/video_store.js";
import { PlaybackStore } from "@/front/stores/playback_store.js";
import { AnnStore } from "@/front/stores/ann_store.js";
// Store initialization
const clickStore = ClickStore();
const playerStore = PlayerStore();
const videoStore = VideoStore();
const playbackStore = PlaybackStore();
const annStore = AnnStore();

import { defineAsyncComponent } from "vue";

// Critical components - load immediately
import IntLayer from "@/front/components/IntLayer.vue";
import DialogLayer from "@/front/components/DialogLayer.vue";
import IntroLayer from "@/front/components/IntroLayer.vue";
import ErrorLayer from "@/front/components/ErrorLayer.vue";
import { useGlobalRenderTracker } from "@/front/composables/GlobalRenderTracker.js";
if (playerStore.isDebugPerformance) {
  useGlobalRenderTracker('MasterLayer', { logLevel: 'none' })
}

const DebugLayer = defineAsyncComponent(() =>
  import("@/front/components/DebugLayer.vue")
);

const AfkLayer = defineAsyncComponent(() =>
  import("@/front/components/AfkLayer.vue")
);

// Deferred components - load when needed
const PayLayer = defineAsyncComponent(() =>
  import("@/front/components/PayLayer.vue")
);
const DrawCreationLayer = defineAsyncComponent(() =>
  import("@/front/components/DrawCreationLayer.vue")
);
const GuideLayer = defineAsyncComponent(() =>
  import("@/front/components/GuideLayer.vue")
);
const EndScreenLayer = defineAsyncComponent(() =>
  import("@/front/components/EndScreenLayer.vue")
);
const MagicMenuLayer = defineAsyncComponent(() =>
  import("@/front/components/MagicMenuLayer.vue")
);
const ScreenshotLayer = defineAsyncComponent(() =>
  import("@/front/components/ScreenshotLayer.vue")
);
const ResumeLayer = defineAsyncComponent(() =>
  import("@/front/components/ResumeLayer.vue")
);

// Computed properties
const showIntroLayer = computed(() => {
  if (props.autoplay) {
    return false
  }
  return (
    playerStore.initialized &&
    !playerStore.error &&
    !playerStore.started &&
    !clickStore.magicMenuActive &&
    !playerStore.branching &&
    playerStore.branchCount == 0
  );
});

const props = defineProps({
  isMenu: Boolean,
  isEnd: Boolean,
  isNormal: Boolean,
  autoplay: Boolean,
  quickStartHighlightTarget: {
    type: String,
    default: "",
  },
});
const devDebugOpen = ref(false);

onMounted(() => { 
  setTimeout(() => {
    if (!annStore.usesFontAwesome) {
      annStore.anns.forEach(ann => {
        let annstr = JSON.stringify(ann)
        if (annstr.includes('<i') && annstr.includes('fa-')) {
          annStore.usesFontAwesome = true
          log.info(2,"🔊 Found font awesome in ann", JSON.parse(annstr))
        }
      })
    }
    if (annStore.usesFontAwesome) {
      const script = document.createElement('script')
      script.src = 'https://kit.fontawesome.com/9b06a44648.js'
      script.crossOrigin = 'anonymous'
      document.head.appendChild(script)
    } else {
      log.info(2,"🔊 No font awesome found")
    }
  }, 500)
}) 
</script>

<template>
  <div
    id="master-layer"
    class="no-scrollbar"
    @keydown.capture.stop
    @keyup.capture.stop
    @keypress.capture.stop
    :class="{ 'border-2 border-green-500': playerStore.isDebug }"
  >
    <Transition name="fade" class="mb-10" style="--animate-duration: 500ms">
      <PayLayer v-if="clickStore.payDialog" />
    </Transition>

    <ErrorLayer v-if="playerStore.error" />

    <DrawCreationLayer
      v-else-if="clickStore.isDrawing && !clickStore.questionActive"
    />

    <GuideLayer v-else-if="clickStore.guideActive" />

    <AfkLayer v-else-if="clickStore.afkActive" />

    <IntLayer
      v-else-if="!playerStore.error && isNormal && playerStore.ready"
      v-show="
        playerStore.started &&
        !playerStore.branching &&
        !clickStore.magicMenuActive
      "
      :quick-start-highlight-target="props.quickStartHighlightTarget"
    />

    <EndScreenLayer
      v-if="
        (isEnd || clickStore.endScreenActive) && !clickStore.magicMenuActive
      "
    />

    <!-- Used for editing the magic menu, playback menu is down in DialogLayer -->
    <MagicMenuLayer v-else-if="isMenu || clickStore.magicMenuActive" />

    <DialogLayer v-if="playerStore.started && videoStore.video.id" />

    <ResumeLayer v-else-if="clickStore.resumeDialog" />

    <IntroLayer v-else-if="showIntroLayer" />

    <Transition
      :name="playerStore.branchTransition || 'fade'"
      mode="out-in"
      appear
      style="--animate-duration: 1s"
      enter-active-class=""
      enter-from-class=""
      enter-to-class=""
    >
      <!-- <Suspense> -->
      <ScreenshotLayer
        v-if="playerStore.branching && playerStore.currentScreenshot"
      />
      <!-- </Suspense> -->
    </Transition>

    <!-- DEBUG SECTION -->
    <DebugLayer v-if="playerStore.isDebug" />
  </div>
</template>

<style scoped>
.loading-placeholder {
  /* Add any loading placeholder styles */
  min-height: 100px;
}
</style>
